/**
 * Cliente API para obtener temas desde el backend de ACV (acvapp)
 * Mismo patrón que aiRecording/themesApi.ts
 * Prioridad URL: REACT_APP_ACV_BACKEND_API, window.ACV_API_URL, database=prod/preprod desde URL, hostname, fallback.
 */
import axios from 'axios';

const ACV_API_BASE_URL = 'https://acvnns-api-produc.acv.app';

class ThemesApi {
  private getApiBaseUrl(): string {
    const win = typeof window !== 'undefined' ? window : null;
    const env = process.env;

    if (env.REACT_APP_ACV_BACKEND_API) {
      const url = (env.REACT_APP_ACV_BACKEND_API as string).trim();
      if (url) return url;
    }
    if (win && (win as any).ACV_API_URL) {
      return (win as any).ACV_API_URL;
    }
    return ACV_API_BASE_URL;
  }

  private getTenantSlug(): string | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    let slug = params.get('slug');
    if (slug) return slug.replace(/^\?slug=/, '');
    const stored = localStorage.getItem('slug');
    if (stored) return stored.replace(/^\?slug=/, '');
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user?.slug) return (user.slug as string).replace(/^\?slug=/, '');
      }
      const msgStr = localStorage.getItem('msg');
      if (msgStr) {
        const msg = JSON.parse(msgStr);
        if (msg?.user?.slug) return (msg.user.slug as string).replace(/^\?slug=/, '');
      }
    } catch (_) {}
    return null;
  }

  private getAccessToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('accessToken') || localStorage.getItem('access_token') || localStorage.getItem('token') || null;
  }

  private getUserEmail(): string | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('owner') || params.get('email') || params.get('userEmail');
    if (fromUrl) return fromUrl;
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) return userEmail;
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user?.mail) return user.mail;
        if (user?.email) return user.email;
      }
      const msgStr = localStorage.getItem('msg');
      if (msgStr) {
        const msg = JSON.parse(msgStr);
        if (msg?.user?.mail) return msg.user.mail;
        if (msg?.user?.email) return msg.user.email;
      }
    } catch (_) {}
    return null;
  }

  private handleApiResponse(response: any): any {
    let data = response?.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
        if (typeof data === 'string') data = JSON.parse(data);
      } catch (_) {
        return null;
      }
    }
    if (data?.data?.theme) return data.data.theme;
    if (data?.theme) return data.theme;
    if (data?.result) return data.result;
    if (response?.result) return response.result;
    if (data && typeof data === 'object' && data.id) return data;
    return null;
  }

  async getModuleTheme(moduleId: string): Promise<any> {
    const base = this.getApiBaseUrl();
    const slug = this.getTenantSlug();
    if (!slug) return null;
    let url = `${base}/api/themes/user/module/${moduleId}?slug=${encodeURIComponent(slug)}`;
    const email = this.getUserEmail();
    if (email) url += `&email=${encodeURIComponent(email)}&userEmail=${encodeURIComponent(email)}`;
    const token = this.getAccessToken();
    try {
      const res = await axios.get(url, {
        headers: token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' },
        validateStatus: (s) => s < 500,
      });
      const theme = this.handleApiResponse(res);
      if (theme && theme.id) return theme;
      return null;
    } catch (e) {
      // console.warn('[ThemesApi] getModuleTheme error:', e);
      return null;
    }
  }

  async getThemeById(themeId: string): Promise<any> {
    const base = this.getApiBaseUrl();
    const slug = this.getTenantSlug();
    if (!slug) return null;
    let url = `${base}/api/themes/${themeId}?slug=${encodeURIComponent(slug)}`;
    const email = this.getUserEmail();
    if (email) url += `&email=${encodeURIComponent(email)}`;
    const token = this.getAccessToken();
    try {
      const res = await axios.get(url, {
        headers: token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' },
        validateStatus: (s) => s < 500,
      });
      if (res.status >= 400) return null;
      const theme = this.handleApiResponse(res);
      if (theme && theme.id) return theme;
      return null;
    } catch (e) {
      // console.warn('[ThemesApi] getThemeById error:', e);
      return null;
    }
  }
}

export const ThemesApiClient = new ThemesApi();
